import { type FormEvent, useEffect, useRef, useState } from "react";

import { type IngredientView, type LoggedMeal, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { mealsCopy } from "../lib/i18n/meals";

type Props = {
  householdId: string;
  meal: LoggedMeal;
  onSaved: (updated: LoggedMeal) => void;
  onCancel: () => void;
};

// Parses a numeric field input. Empty → null; NaN guarded; clamped.
function parseNum(raw: string, opts: { int?: boolean; min: number; max: number }): number | null {
  if (raw.trim() === "") return null;
  const n = Number(raw.replace(",", "."));
  if (!Number.isFinite(n)) return null;
  const clamped = Math.max(opts.min, Math.min(opts.max, n));
  return opts.int ? Math.round(clamped) : clamped;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function sumIngredients(ings: IngredientView[]) {
  return ings.reduce(
    (a, x) => ({
      kcal: a.kcal + x.kcal,
      proteinG: a.proteinG + (x.proteinG ?? 0),
      fatG: a.fatG + (x.fatG ?? 0),
      carbsG: a.carbsG + (x.carbsG ?? 0),
    }),
    { kcal: 0, proteinG: 0, fatG: 0, carbsG: 0 },
  );
}

export function EditMealModal({ householdId, meal, onSaved, onCancel }: Props) {
  const { lang } = useLang();
  const t = mealsCopy[lang];
  const ref = useRef<HTMLDialogElement | null>(null);
  const [name, setName] = useState(meal.name);
  const [kcal, setKcal] = useState(String(meal.kcal));
  const [protein, setProtein] = useState(meal.proteinG !== null ? String(meal.proteinG) : "");
  const [fat, setFat] = useState(meal.fatG !== null ? String(meal.fatG) : "");
  const [carbs, setCarbs] = useState(meal.carbsG !== null ? String(meal.carbsG) : "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nested-meal ingredient editing (ADR-0010).
  const [ingredients, setIngredients] = useState<IngredientView[] | null>(null);
  const [swap, setSwap] = useState<{ ingId: string; text: string } | null>(null);
  const [swapping, setSwapping] = useState(false);
  // Portion captured on focus → blur rescales kcal/macros by the net change.
  const portionBaseRef = useRef<number | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!node.open) node.showModal();
    return () => {
      if (node.open) node.close();
    };
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onCancelNative = (e: Event) => {
      e.preventDefault();
      onCancel();
    };
    node.addEventListener("cancel", onCancelNative);
    return () => node.removeEventListener("cancel", onCancelNative);
  }, [onCancel]);

  // Lazy-load ingredients for a nested meal.
  useEffect(() => {
    if (!meal.hasIngredients) return;
    let cancelled = false;
    api
      .getMealIngredients(householdId, meal.id)
      .then((rows) => {
        if (!cancelled) setIngredients(rows);
      })
      .catch(() => {
        // non-fatal — modal falls back to flat editing
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, meal.id, meal.hasIngredients]);

  function updateIngredient(ingId: string, patch: Partial<IngredientView>) {
    setIngredients((prev) =>
      prev ? prev.map((g) => (g.id === ingId ? { ...g, ...patch } : g)) : prev,
    );
  }

  // After a portion edit, rescale that ingredient's kcal + macros.
  function rescalePortion(ingId: string, base: number | null) {
    if (base === null || base <= 0) return;
    setIngredients((prev) =>
      prev
        ? prev.map((g) => {
            if (g.id !== ingId) return g;
            const cur = g.portionGrams;
            if (cur === null || cur <= 0 || cur === base) return g;
            const f = cur / base;
            return {
              ...g,
              kcal: Math.round(g.kcal * f),
              proteinG: g.proteinG !== null ? round1(g.proteinG * f) : null,
              fatG: g.fatG !== null ? round1(g.fatG * f) : null,
              carbsG: g.carbsG !== null ? round1(g.carbsG * f) : null,
            };
          })
        : prev,
    );
  }

  async function deleteIngredient(ingId: string) {
    setError(null);
    try {
      await api.deleteIngredient(householdId, meal.id, ingId);
      setIngredients((prev) => (prev ? prev.filter((g) => g.id !== ingId) : prev));
      setSwap(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.editGenericError);
    }
  }

  async function applySwap() {
    if (!swap) return;
    const newName = swap.text.trim();
    if (newName.length < 2) return;
    setSwapping(true);
    setError(null);
    try {
      const parsed = await api.parseMeal(newName);
      const est = parsed.items[0];
      if (!est) {
        setError(t.editGenericError);
        return;
      }
      updateIngredient(swap.ingId, {
        name: est.name,
        kcal: est.kcal,
        proteinG: est.proteinG,
        fatG: est.fatG,
        carbsG: est.carbsG,
        portionGrams: est.portionGrams,
      });
      setSwap(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.editGenericError);
    } finally {
      setSwapping(false);
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (meal.hasIngredients && ingredients) {
        // Persist every ingredient; each PATCH re-sums the dish server-side.
        for (const ing of ingredients) {
          await api.updateIngredient(householdId, meal.id, ing.id, {
            name: ing.name,
            portionGrams: ing.portionGrams,
            kcal: ing.kcal,
            proteinG: ing.proteinG,
            fatG: ing.fatG,
            carbsG: ing.carbsG,
          });
        }
        // Name lives on the meal row; kcal/macros are already recalculated.
        const updated = await api.updateMeal(householdId, meal.id, {
          name: name.trim() || meal.name,
        });
        onSaved(updated);
        return;
      }
      const updated = await api.updateMeal(householdId, meal.id, {
        name: name.trim() || meal.name,
        kcal: Number(kcal),
        proteinG: protein === "" ? null : Number(protein),
        fatG: fat === "" ? null : Number(fat),
        carbsG: carbs === "" ? null : Number(carbs),
      });
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.editGenericError);
    } finally {
      setSubmitting(false);
    }
  }

  const isNested = meal.hasIngredients;
  const dishTotal = ingredients ? sumIngredients(ingredients) : null;
  // 16px font-size is the threshold below which iOS Safari auto-zooms on
  // focus; keeping all editable inputs at >=16px prevents the "page jumps
  // and zooms" UX glitch reported during APK testing.
  const fieldClass =
    "h-11 px-3 bg-[var(--surface-2)] rounded-md text-[16px] shadow-[inset_0_0_0_1px_var(--nav-border)] focus:shadow-[inset_0_0_0_2px_var(--accent)] outline-none";
  const smallFieldClass =
    "h-10 px-2.5 bg-[var(--surface)] rounded-md text-[16px] shadow-[inset_0_0_0_1px_var(--nav-border)] focus:shadow-[inset_0_0_0_2px_var(--accent)] outline-none w-full";
  // Renders the compact "PORCJA (G)" eyebrow over each input so the column
  // is identifiable on dark backgrounds where the placeholder text reads
  // as illegible blob (user feedback: "te 3 wartosci bo nie sa podpisane").
  const eyebrowStyle = {
    fontFamily: "var(--font-mono)",
    fontSize: 9,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    color: "var(--muted)",
    fontWeight: 600,
  };

  return (
    <dialog
      ref={ref}
      aria-labelledby="edit-meal-title"
      className="bg-[var(--surface)] rounded-xl p-5 w-full max-w-md backdrop:bg-black/70"
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
        // 90dvh (dynamic viewport) rather than 80vh: when iOS keyboard
        // opens for kcal / macro / portion inputs, dvh shrinks with the
        // visible area so the form stays scrollable instead of having
        // its bottom fields pushed under the keyboard (handoff §3.3).
        style={{ maxHeight: "90dvh", overflowY: "auto" }}
      >
        <h2
          id="edit-meal-title"
          className="text-[length:var(--text-h3)] font-bold tracking-[-0.02em] text-[var(--ink)]"
        >
          {t.editTitle}
        </h2>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm text-[var(--ink-2)]">{t.editNameLabel}</span>
          <input
            type="text"
            required
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldClass}
          />
        </label>

        {isNested ? (
          /* Nested meal → edit each ingredient; dish total is their sum. */
          <div className="flex flex-col gap-2">
            <span className="text-caption text-[var(--muted)] uppercase tracking-wider">
              {t.confirmIngredientsLabel}
            </span>
            {ingredients === null ? (
              <p className="text-body-sm text-[var(--muted)]">{t.editSaving}</p>
            ) : (
              ingredients.map((ing) => {
                const swapActive = swap?.ingId === ing.id;
                return (
                  <div
                    key={ing.id}
                    className="flex flex-col gap-1.5 rounded-md p-2.5"
                    style={{ background: "var(--surface-2)" }}
                  >
                    <label className="flex flex-col gap-1">
                      <span style={eyebrowStyle}>{t.confirmFieldName}</span>
                      <input
                        type="text"
                        maxLength={200}
                        value={ing.name}
                        onChange={(e) => updateIngredient(ing.id, { name: e.target.value })}
                        className={smallFieldClass}
                      />
                    </label>
                    <div className="grid grid-cols-3 gap-1.5">
                      <label className="flex flex-col gap-1">
                        <span style={eyebrowStyle}>{t.confirmFieldPortion}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={ing.portionGrams ?? ""}
                          onFocus={() => {
                            portionBaseRef.current = ing.portionGrams;
                          }}
                          onChange={(e) =>
                            updateIngredient(ing.id, {
                              portionGrams: parseNum(e.target.value, { min: 0, max: 5000 }),
                            })
                          }
                          onBlur={() => rescalePortion(ing.id, portionBaseRef.current)}
                          className={smallFieldClass}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span style={eyebrowStyle}>{t.confirmFieldKcal}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={ing.kcal}
                          onChange={(e) =>
                            updateIngredient(ing.id, {
                              kcal:
                                parseNum(e.target.value, { int: true, min: 0, max: 10000 }) ?? 0,
                            })
                          }
                          className={smallFieldClass}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span style={eyebrowStyle}>{t.confirmFieldProtein}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={ing.proteinG ?? ""}
                          onChange={(e) =>
                            updateIngredient(ing.id, {
                              proteinG: parseNum(e.target.value, { min: 0, max: 500 }),
                            })
                          }
                          className={smallFieldClass}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span style={eyebrowStyle}>{t.confirmFieldFat}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={ing.fatG ?? ""}
                          onChange={(e) =>
                            updateIngredient(ing.id, {
                              fatG: parseNum(e.target.value, { min: 0, max: 500 }),
                            })
                          }
                          className={smallFieldClass}
                        />
                      </label>
                      <label className="flex flex-col gap-1">
                        <span style={eyebrowStyle}>{t.confirmFieldCarbs}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={ing.carbsG ?? ""}
                          onChange={(e) =>
                            updateIngredient(ing.id, {
                              carbsG: parseNum(e.target.value, { min: 0, max: 1000 }),
                            })
                          }
                          className={smallFieldClass}
                        />
                      </label>
                    </div>
                    {swapActive ? (
                      <div className="flex gap-1.5">
                        <input
                          type="text"
                          placeholder={t.confirmSwapPlaceholder}
                          value={swap.text}
                          onChange={(e) => setSwap({ ingId: ing.id, text: e.target.value })}
                          className={smallFieldClass}
                        />
                        <button
                          type="button"
                          onClick={applySwap}
                          disabled={swapping}
                          className="h-10 px-3 bg-[var(--accent)] text-[var(--accent-ink)] rounded-md text-caption font-medium disabled:opacity-50 shrink-0"
                        >
                          {swapping ? t.confirmSwapping : t.confirmSwapApply}
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setSwap({ ingId: ing.id, text: "" })}
                          className="text-caption text-[var(--accent)] font-medium"
                        >
                          {t.confirmSwap}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteIngredient(ing.id)}
                          className="text-caption text-[var(--muted)]"
                        >
                          {t.confirmRemove}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
            {dishTotal ? (
              <p className="text-caption text-[var(--muted)]">
                {t.confirmDishSum}: {dishTotal.kcal} kcal
              </p>
            ) : null}
          </div>
        ) : (
          /* Flat meal → direct kcal + macro editing. */
          <>
            <label className="flex flex-col gap-1.5">
              <span className="text-body-sm text-[var(--ink-2)]">{t.editKcalLabel}</span>
              <input
                type="number"
                inputMode="numeric"
                required
                min={0}
                max={10000}
                value={kcal}
                onChange={(e) => setKcal(e.target.value)}
                className={fieldClass}
              />
            </label>

            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  [t.editProteinLabel, protein, setProtein],
                  [t.editFatLabel, fat, setFat],
                  [t.editCarbsLabel, carbs, setCarbs],
                ] as const
              ).map(([label, value, setter]) => (
                <label key={label} className="flex flex-col gap-1">
                  <span className="text-caption text-[var(--muted)] uppercase tracking-wider">
                    {label}
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={0.1}
                    placeholder={t.editGramPlaceholder}
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    className={fieldClass}
                  />
                </label>
              ))}
            </div>
          </>
        )}

        {error ? <p className="text-caption text-[var(--color-error)]">{error}</p> : null}

        <div className="flex flex-col gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="h-11 px-4 bg-[var(--accent)] text-[var(--accent-ink)] rounded-md font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? t.editSaving : t.editSave}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="h-11 px-4 text-[var(--ink-2)] rounded-md transition-colors hover:bg-[var(--surface-2)]"
          >
            {t.editCancel}
          </button>
        </div>
      </form>
    </dialog>
  );
}
